/** X-Device-Id：16–64 位，A-Za-z0-9 及 . _ : - */
export const DEVICE_ID_RE = /^[A-Za-z0-9._:-]{16,64}$/
export const DEVICE_ID_KEY = 'x-device-id'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export function isValidDeviceId(value: unknown): value is string {
  return typeof value === 'string' && DEVICE_ID_RE.test(value)
}

/** UUID v4；兼容 Chrome 85 / Safari 13（无 crypto.randomUUID） */
export function createDeviceId(): string {
  const c = globalThis.crypto
  if (typeof c?.randomUUID === 'function') return c.randomUUID()

  const bytes = new Uint8Array(16)
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)

  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function readLocal(): string | null {
  if (!import.meta.client) return null
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY)
    return isValidDeviceId(stored) ? stored : null
  } catch {
    return null
  }
}

function persist(id: string, cookie: { value: string | null | undefined }) {
  if (cookie.value !== id) cookie.value = id
  if (!import.meta.client) return
  try {
    localStorage.setItem(DEVICE_ID_KEY, id)
  } catch {
    // private mode 等忽略
  }
}

/**
 * 前端生成并持久化设备 ID：cookie 供 SSR 读取，localStorage 作客户端备份。
 * 首次生成后固定，后续请求复用同一值。
 */
export function resolveDeviceId(): string {
  try {
    const state = useState<string | null>(DEVICE_ID_KEY, () => null)
    if (isValidDeviceId(state.value)) return state.value

    const cookie = useCookie<string | null>(DEVICE_ID_KEY, {
      sameSite: 'lax',
      maxAge: COOKIE_MAX_AGE,
      path: '/',
    })

    const id =
      (isValidDeviceId(cookie.value) ? cookie.value : null) ?? readLocal() ?? createDeviceId()

    state.value = id
    persist(id, cookie)
    return id
  } catch {
    return createDeviceId()
  }
}
